import { useCallback } from "react";
import dateformat from "dateformat";
import XLSX from "xlsx-js-style";
import JSZip from "jszip";

import {
  DispositionType,
  Shipment,
  ShippingAddress,
  ShippingRecord,
  Warehouse,
} from "$types";
import { DEFAULT_DATE_FORMAT, toTime } from "$utils/utils";
import { usePackageTypesQuery } from "./usePackageTypesQuery";
import { EntrepotService, ParcelService, UserServices } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import {
  createDeliveryAddressFromOp,
  getShippingRecordWishedDeliveryDate,
} from "$utils/typeConstants/ShippingRecord";
import useAuth from "./useAuth";

const tableHeaderConfig = {
  border: {
    top: { style: "thin", color: { auto: 1 } },
    left: { style: "thin", color: { auto: 1 } },
    right: { style: "thin", color: { auto: 1 } },
    bottom: { style: "thin", color: { auto: 1 } },
  },
  fill: {
    patternType: "solid",
    fgColor: { rgb: "457DD9" }, // theme.lightBlue
  },
  font: {
    color: { rgb: "FFFFFF" },
  },
};

const tableCellConfig = {
  border: {
    top: { style: "thin", color: { auto: 1 } },
    left: { style: "thin", color: { auto: 1 } },
    right: { style: "thin", color: { auto: 1 } },
    bottom: { style: "thin", color: { auto: 1 } },
  },
};

const emptyLine = {
  A: "",
};

const dividerLineConfig = {
  fill: {
    patternType: "solid",
    fgColor: { rgb: "A9A9A9" },
  },
};

const fetchEntrepotByName = async (name?: string) => {
  if (!name || name === "N/A") return undefined;
  const filters = { name };
  const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
  let getWarehouses: any = await queryClient.getQueryData(queryKey);
  if (!getWarehouses) {
    getWarehouses = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => EntrepotService.getEntrepots(filters),
      gcTime: 5 * 60 * 1000,
    });
    queryClient.setQueryData(queryKey, getWarehouses);
  }
  return getWarehouses?.data?.[0];
};

// Warehouse.users is always empty from the entrepot list/search endpoint
// (the "users" relation isn't included there) — an entrepot's contact
// people are only available via this separate user-search endpoint,
// filtered by warehouse id, the same way the entrepot profile page's
// "Contacts" section fetches them.
const fetchEntrepotContact = async (token: string, entrepotId?: string) => {
  if (!entrepotId) return undefined;
  const filters = { warehouse: entrepotId, isDisable: false };
  const queryKey = [CacheInvalidationAction.GET_USERS, filters];
  let users: any = await queryClient.getQueryData(queryKey);
  if (!users) {
    users = await queryClient.fetchQuery({
      queryKey,
      queryFn: () => UserServices.FetchUsers(token, filters),
      gcTime: 5 * 60 * 1000,
    });
    queryClient.setQueryData(queryKey, users);
  }
  return users?.[0];
};

// `delivery.am`/`delivery.pm` are lists of half-hour slots (e.g. [9, 9.5, 10]),
// not a simple [start, end] pair — group consecutive slots into ranges the
// same way the entrepot profile page does (see EntrepotInfo.tsx).
const formatEntrepotHours = (warehouse?: Warehouse) => {
  const slots = [
    ...(warehouse?.delivery?.am || []),
    ...(warehouse?.delivery?.pm || []),
  ]
    .sort((a, b) => a - b)
    .filter((value, index, all) => all.indexOf(value) === index);
  if (!slots.length) return "-";

  const ranges: string[] = [];
  let rangeStart = slots[0];
  let rangeEnd = slots[0];
  slots.slice(1).forEach((slot, index) => {
    const isLast = index === slots.length - 2;
    if (slot - rangeEnd <= 0.5) {
      rangeEnd = slot;
      if (isLast)
        ranges.push(`${toTime(rangeStart)} - ${toTime(rangeEnd + 0.5)}`);
    } else {
      ranges.push(`${toTime(rangeStart)} - ${toTime(rangeEnd + 0.5)}`);
      rangeStart = slot;
      rangeEnd = slot;
      if (isLast)
        ranges.push(`${toTime(rangeStart)} - ${toTime(rangeEnd + 0.5)}`);
    }
  });
  if (slots.length === 1)
    ranges.push(`${toTime(rangeStart)} - ${toTime(rangeEnd + 0.5)}`);

  return ranges.join(" / ");
};

const formatContactName = (user?: { firstname?: string; lastname?: string }) =>
  user?.firstname && user?.lastname
    ? `${user.firstname} ${user.lastname}`
    : "-";

// The template has three delivery slots side by side (LIVRAISON 1/2/3, in
// columns G/H/I), each with its own VLOOKUP-into-address-book formulas keyed
// on that column's row 9. A dossier with several distinct delivery locations
// (e.g. several chantiers, or a mix of chantier + entrepôt) gets one column
// per location instead of only ever filling the first one.
const AFFRETEMENT_DELIVERY_COLUMNS = ["G", "H", "I"];

const normalizeText = (value: string) => value.trim().toLowerCase();

// The shipment's OP (and through it, the EDB that generated it) is the
// source of truth for where a product actually needs to go — the same
// helper the rest of the app uses (OP details, tracking table, disputes...)
// to turn shipType/chantier/dockyard/EDB contact fields into an address.
// shipment.toAddress is only a snapshot taken at shipment-creation time and
// can go stale or, for OTHER_PLACE dispositions, was never handled here at
// all (it used to silently fall into the entrepôt branch below).
const deliveryAddressForShipment = (shipment: Shipment) =>
  shipment.op ? createDeliveryAddressFromOp(shipment.op) : undefined;

// Grouping key: the resolved address itself (name + street + zip + city),
// not just entityId — a single chantier (or entrepôt) record can be reused
// across many OPs over its lifetime while each OP still carries its own
// exact shipAdress/shipPostalCode/shipCity, so two OPs pointing at the same
// chantier entity can still be two genuinely different delivery points and
// must land in separate LIVRAISON columns.
const deliveryGroupKey = (shipment: Shipment) => {
  const address = deliveryAddressForShipment(shipment);
  if (!address) return undefined;
  return [
    address.name,
    address.street1,
    address.street2,
    address.zip,
    address.city,
  ]
    .map((part) => normalizeText((part || "").toString()))
    .join("|");
};

const groupShipmentsByDelivery = (shipments: Shipment[]) => {
  const groups: Shipment[][] = [];
  shipments.forEach((shipment) => {
    const key = deliveryGroupKey(shipment);
    const group = groups.find(
      (candidate) => deliveryGroupKey(candidate[0]) === key
    );
    if (group) group.push(shipment);
    else groups.push([shipment]);
  });
  return groups;
};

const resolveDeliveryDetails = async (shipment: Shipment, token: string) => {
  const { op } = shipment;
  // Only the entrepôt-based dispositions (NEAREST_WAREHOUSE,
  // SUBCONTRACTED_WAREHOUSE) have a full Warehouse record worth fetching for
  // opening hours and a registered on-site contact — ON_DOCKYARD and
  // OTHER_PLACE resolve entirely from the OP/EDB fields already embedded on
  // the shipment (createDeliveryAddressFromOp + the EDB's own recovery contact).
  const isEntrepot =
    op?.shipType !== DispositionType.ON_DOCKYARD &&
    op?.shipType !== DispositionType.OTHER_PLACE;

  if (!isEntrepot) {
    const address = deliveryAddressForShipment(shipment);
    return {
      name: address?.name || "-",
      address:
        `${address?.street1 || ""} ${address?.street2 || ""}`.trim() || "-",
      zip: address?.zip || "-",
      city: address?.city || "-",
      hours: "-",
      contact: formatContactName({
        firstname: op?.edb?.firstnameContactRecovery,
        lastname: op?.edb?.lastnameContactRecovery,
      }),
      phone: address?.phone || "-",
      email: address?.email || "-",
    };
  }

  const deliveryEntrepot = await fetchEntrepotByName(op?.shipEntrepotName);
  const entrepotContact = await fetchEntrepotContact(
    token,
    deliveryEntrepot?._id
  );

  return {
    name: deliveryEntrepot?.name || "-",
    address:
      `${deliveryEntrepot?.location?.adress || ""} ${
        deliveryEntrepot?.location?.complement || ""
      }`.trim() || "-",
    zip: deliveryEntrepot?.location?.cp || "-",
    city: deliveryEntrepot?.location?.city || "-",
    hours: formatEntrepotHours(deliveryEntrepot),
    contact: formatContactName(entrepotContact),
    phone: entrepotContact?.phone || "-",
    email: entrepotContact?.email || "-",
  };
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

// Overwrites a single cell's value in raw worksheet XML, dropping any formula
// it had. Keeps the cell's existing style unless styleId is provided (used to
// force black text on shippingRecord-filled cells without touching template
// placeholder styles on other cells that share the same red/green fonts).
const setSheetCellValue = (
  xml: string,
  address: string,
  value: string | number,
  styleId?: number
) => {
  // Self-closing form must be tried first: for a self-closing tag like
  // `<c r="D21" s="161"/>`, the "has content" alternative below would still
  // match (its `[^>]*>` swallows the `/` and the `>`), then greedily consume
  // everything up to the next unrelated `</c>` anywhere later in the sheet,
  // deleting every cell in between on replace.
  const cellMatch = xml.match(
    new RegExp(`<c r="${address}"[^>]*/>|<c r="${address}"[^>]*>[\\s\\S]*?</c>`)
  );
  if (!cellMatch) return xml;
  const original = cellMatch[0];
  const styleMatch = original.match(/ s="(\d+)"/);
  const style =
    styleId !== undefined
      ? ` s="${styleId}"`
      : styleMatch
        ? ` s="${styleMatch[1]}"`
        : "";
  const replacement =
    typeof value === "number"
      ? `<c r="${address}"${style}><v>${value}</v></c>`
      : `<c r="${address}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(
          value
        )}</t></is></c>`;
  return xml.replace(original, replacement);
};

// Template data cells use red (livraison) / green (colisage) / red sz14 (C4)
// fonts as "fill-me" hints. When we write shippingRecord values we point those
// cells at black styles instead — without recoloring the template's own labels
// that still reference the original fonts.
// Style 98 is already the black twin of the red livraison style (97). C4 and
// colisage need new xfs appended to styles.xml at export time.
const AFFRETEMENT_LIVRAISON_BLACK_STYLE = 98;

const addAffretementFilledCellStyles = (stylesXml: string) => {
  const fontsOpen = stylesXml.match(/<fonts count="(\d+)">/);
  const cellXfsOpen = stylesXml.match(/<cellXfs count="(\d+)">/);
  if (!fontsOpen || !cellXfsOpen) {
    return {
      stylesXml,
      dossierStyleId: AFFRETEMENT_LIVRAISON_BLACK_STYLE,
      livraisonStyleId: AFFRETEMENT_LIVRAISON_BLACK_STYLE,
      colisageStyleId: AFFRETEMENT_LIVRAISON_BLACK_STYLE,
    };
  }

  const fontCount = Number(fontsOpen[1]);
  const cellXfCount = Number(cellXfsOpen[1]);
  // font 35 in the template is already bold Calibri 16 with no color (= black).
  const dossierFontId = 35;
  const colisageFontId = fontCount;

  let next = stylesXml.replace(
    `<fonts count="${fontCount}">`,
    `<fonts count="${fontCount + 1}">`
  );
  next = next.replace(
    "</fonts>",
    `<font><b/><sz val="12"/><color rgb="FF000000"/><name val="Arial"/><family val="2"/></font></fonts>`
  );

  const dossierStyleId = cellXfCount;
  const colisageStyleId = cellXfCount + 1;
  next = next.replace(
    `<cellXfs count="${cellXfCount}">`,
    `<cellXfs count="${cellXfCount + 2}">`
  );
  next = next.replace(
    "</cellXfs>",
    `<xf numFmtId="0" fontId="${dossierFontId}" fillId="10" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="left"/></xf>` +
      `<xf numFmtId="0" fontId="${colisageFontId}" fillId="10" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>` +
      `</cellXfs>`
  );

  return {
    stylesXml: next,
    dossierStyleId,
    livraisonStyleId: AFFRETEMENT_LIVRAISON_BLACK_STYLE,
    colisageStyleId,
  };
};

// Resolves a worksheet's file path from its visible name by reading
// xl/workbook.xml + its rels, instead of assuming a fixed "sheetN.xml"
// name, since re-exporting the template can renumber/reorder sheet files.
const resolveSheetPath = async (zip: JSZip, sheetName: string) => {
  const workbookXml = await zip.file("xl/workbook.xml").async("string");
  const sheetTag = (
    workbookXml.match(/<sheet\b[^>]*\/>/g) || ([] as string[])
  ).find((tag) => tag.includes(`name="${sheetName}"`));
  const relId = sheetTag?.match(/r:id="([^"]+)"/)?.[1];
  const relsXml = await zip.file("xl/_rels/workbook.xml.rels").async("string");
  const relTag = (
    relsXml.match(/<Relationship\b[^>]*\/>/g) || ([] as string[])
  ).find((tag) => tag.includes(`Id="${relId}"`));
  const target = relTag?.match(/Target="([^"]+)"/)?.[1];
  return target ? `xl/${target}` : undefined;
};

// Overwriting formula cells (VLOOKUP / SUM / …) leaves xl/calcChain.xml
// pointing at cells that no longer contain formulas. Excel treats that as
// workbook corruption and shows the "We found a problem with some content"
// recovery dialog. Drop the calc chain (and its Content_Types / rels
// entries) entirely — Excel rebuilds it on open.
const removeCalcChain = async (zip: JSZip) => {
  zip.remove("xl/calcChain.xml");

  const contentTypesFile = zip.file("[Content_Types].xml");
  if (contentTypesFile) {
    const contentTypesXml = await contentTypesFile.async("string");
    zip.file(
      "[Content_Types].xml",
      contentTypesXml.replace(
        /<Override[^>]*PartName="\/xl\/calcChain\.xml"[^>]*\/>/,
        ""
      )
    );
  }

  const workbookRelsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (workbookRelsFile) {
    const relsXml = await workbookRelsFile.async("string");
    zip.file(
      "xl/_rels/workbook.xml.rels",
      relsXml.replace(/<Relationship[^>]*Target="calcChain\.xml"[^>]*\/>/, "")
    );
  }
};

// The "Expéditeur" sheet is the freight forwarder's pickup address book: if
// a pickup name already exists there, the template's VLOOKUP formulas fill
// in the rest of the row automatically. Delivery columns are always written
// from Noria data (the Destinataire sheet was removed from the template).
// Columns: Nom(0), Horaires(1), Adresse(2), Adresse1(3), Code postal(4),
// Ville(5), Contact(6), Portable(7), Email(8), Informations(9),
// Complémentaires(10) — matches the VLOOKUP column indexes in the template.
const readAddressBookRows = (
  workbook: XLSX.WorkBook,
  sheetName: string
): string[][] => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 }).slice(1);
};

const dividerLine = {
  A: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  B: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  C: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  D: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  E: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  F: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  G: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
  H: {
    v: "",
    t: "s",
    s: dividerLineConfig,
  },
};

const useShippingRecordExport = (shippingRecord: ShippingRecord) => {
  const { data: packageTypes = [] } = usePackageTypesQuery();
  const { token } = useAuth();
  const exportShippingRecord = useCallback(() => {
    if (!shippingRecord) return;
    const fromAddress = shippingRecord.shipments[0]?.fromAddress;
    const op = shippingRecord.shipments[0]?.op;

    const rows: any = [
      { A: "N° dossier", B: shippingRecord?.code },
      {
        A: "Nom du demandeur",
        B: "Batail-Log",
      },
      {
        A: "Lieu enlèvement",
        B: `${fromAddress.name} - ${fromAddress.street1} ${
          fromAddress.street2 || ""
        } - ${fromAddress.zip} ${fromAddress.city} - ${fromAddress.country}`,
      },
      {
        A: "Date d'enlèvement souhaitée",
        B: shippingRecord?.wishedShippingDate
          ? dateformat(shippingRecord?.wishedShippingDate, DEFAULT_DATE_FORMAT)
          : "N/A",
      },
      {
        A: "Contact sur place",
        B:
          op?.entrepot &&
          // TODO
          // Temporarily, use only the first user. Waiting for validation to use all users for example.
          op?.entrepot?.users &&
          op?.entrepot?.users[0] &&
          op?.entrepot?.users[0]?.firstname &&
          op?.entrepot?.users[0]?.lastname &&
          op?.entrepot?.users[0]?.email &&
          op?.entrepot?.users[0]?.phone
            ? `${op?.entrepot?.users[0]?.firstname || "N/A"} ${
                op?.entrepot?.users[0]?.lastname || "N/A"
              } - ${op?.entrepot?.users[0]?.email || "N/A"} - ${
                op?.entrepot?.users[0]?.phone || "N/A"
              }`
            : "N/A",
      },
      {
        A: "Date de livraison souhaitée",
        B: (() => {
          const wishedDeliveryDate =
            getShippingRecordWishedDeliveryDate(shippingRecord);
          return wishedDeliveryDate
            ? dateformat(wishedDeliveryDate, DEFAULT_DATE_FORMAT)
            : "N/A";
        })(),
      },
      {
        A: "Date de livraison prévue",
        B: shippingRecord?.expectedDeliveryDate
          ? dateformat(
              shippingRecord?.expectedDeliveryDate,
              DEFAULT_DATE_FORMAT
            )
          : "N/A",
      },
      {
        A: "Remarques du dossier",
        B: shippingRecord?.notes || "N/A",
      },
    ];

    if (shippingRecord.shipments.length > 0) {
      // Sort shipments by unique toAddress
      const addressesWithShipments: (Partial<ShippingAddress> & {
        shipments: Shipment[];
      })[] = [];
      shippingRecord.shipments.forEach((shipment) => {
        const foundAddressIndex = addressesWithShipments.findIndex(
          (toAddress) => toAddress.name === shipment.toAddress.name
        );
        if (foundAddressIndex === -1) {
          addressesWithShipments.push({
            ...shipment.toAddress,
            shipments: [shipment],
          });
        } else {
          addressesWithShipments[foundAddressIndex].shipments.push(shipment);
        }
      });

      addressesWithShipments.forEach(async (address, index) => {
        const { shipments } = address;
        const shipment = shipments[0];
        rows.push(dividerLine);

        // Bloc livraison
        if (shipment.op?.shipType === DispositionType.ON_DOCKYARD) {
          const { chantier } = shipment.op;
          if (!chantier) return;
          rows.push(
            {
              A: `Lieu de livraison ${index + 1} (chantier)`,
              B: `${address.name} - ${address.street1} ${
                address.street2 || ""
              } - ${address.zip} ${address.city} - ${address.country}`,
            },
            {
              A: `Type de lieu`,
              B: "Chantier",
            },
            {
              A: `Horaires de livraison`,
              B: "N/A",
            },
            {
              A: `Contraintes de livraison`,
              B: `${chantier?.notes || "N/A"}`,
            },
            {
              A: `Contact sur place`,
              B: shipment.op?.edb
                ? `${shipment.op?.edb?.firstnameContactRecovery || "N/A"} ${
                    shipment.op?.edb?.lastnameContactRecovery || "N/A"
                  } - ${shipment.op?.edb?.emailContactRecovery || "N/A"} - ${
                    shipment.op?.edb?.phoneContactRecovery || "N/A"
                  }`
                : "N/A",
            }
          );
        } else {
          const warehouse = await fetchEntrepotByName(
            shipment.op?.shipEntrepotName
          );

          rows.push(
            {
              A: `Lieu de livraison ${index + 1} (entrepôt)`,
              B: `${address.name} - ${address.street1} ${
                address.street2 || ""
              } - ${address.zip} ${address.city} - ${address.country}`,
            },
            {
              A: `Type de lieu`,
              B: "Entrepôt",
            },
            {
              A: `Horaires de livraison`,
              B: warehouse
                ? `Matin: ${warehouse?.delivery?.am.toString()} - Après-midi: ${warehouse?.delivery?.pm.toString()}`
                : "N/A",
            },
            {
              A: `Contraintes de livraison`,
              B: `${warehouse?.notes || "N/A"}`,
            },
            {
              A: `Contact sur place`,
              B: shipment.op?.edb
                ? `${shipment.op?.edb?.firstnameContactRecovery || "N/A"} ${
                    shipment.op?.edb?.lastnameContactRecovery || "N/A"
                  } - ${shipment.op?.edb?.emailContactRecovery || "N/A"} - ${
                    shipment.op?.edb?.phoneContactRecovery || "N/A"
                  }`
                : "N/A",
            }
          );
        }

        rows.push(emptyLine);

        // Table headers
        rows.push({
          A: {
            v: `Réf préparation entrepôt / Réf OP`,
            t: "s",
            s: tableHeaderConfig,
          },
          B: {
            v: `Type`,
            t: "s",
            s: tableHeaderConfig,
          },
          C: {
            v: `Longueur (cm)`,
            t: "s",
            s: tableHeaderConfig,
          },
          D: {
            v: `Largeur (cm)`,
            t: "s",
            s: tableHeaderConfig,
          },
          E: {
            v: `Hauteur (cm)`,
            t: "s",
            s: tableHeaderConfig,
          },
          F: {
            v: `Poids (kg)`,
            t: "s",
            s: tableHeaderConfig,
          },
          G: {
            v: `Quantité`,
            t: "s",
            s: tableHeaderConfig,
          },
          H: {
            v: `Poids total (kg)`,
            t: "s",
            s: tableHeaderConfig,
          },
        });

        // Bloc par op
        shipments.forEach((shipment) => {
          const { op } = shipment;

          // Tableau colisage
          if (shipment.parcel && shipment.parcel.packages) {
            const { parcel } = shipment;
            const { packages } = parcel;

            // Packages
            packages.forEach((packageItem, index) => {
              const packageType = packageTypes.find(
                (item) => item.id === packageItem.typeId
              );
              rows.push({
                A: {
                  v:
                    index === 0
                      ? `${op?.refPreparationEntrepot || "N/A"} / ${
                          op?.reference || "N/A"
                        }`
                      : "",
                  t: "s",
                  s: tableCellConfig,
                },
                B: {
                  v: packageType?.label || "",
                  t: "s",
                  s: tableCellConfig,
                },
                C: {
                  v: packageItem.length || "",
                  t: "s",
                  s: tableCellConfig,
                },
                D: {
                  v: packageItem.width || "",
                  t: "s",
                  s: tableCellConfig,
                },
                E: {
                  v: packageItem.height || "",
                  t: "s",
                  s: tableCellConfig,
                },
                F: {
                  v: packageItem.weight || "",
                  t: "s",
                  s: tableCellConfig,
                },
                G: {
                  v: packageItem.quantity || "",
                  t: "s",
                  s: tableCellConfig,
                },
                H: {
                  v: packageItem.totalWeight || "",
                  t: "s",
                  s: tableCellConfig,
                },
              });
            });
          }
        });

        const totalValues = shipments.reduce(
          (acc: any, shipment) => {
            const { parcel } = shipment;
            acc.loadingMeters += parcel?.loadingMeters || 0;
            acc.height =
              (parcel?.height || 0) > acc.height ? parcel?.height : acc.height;
            acc.quantity += parcel?.quantity || 0;
            acc.weight += parcel?.weight || 0;
            return acc;
          },
          { loadingMeters: 0, height: 0, quantity: 0, weight: 0 }
        );

        // Empty row
        rows.push(emptyLine);

        // Total headers
        rows.push({
          A: "",
          B: "",
          C: {
            v: `COLISAGE TOTAL ENVOI`,
            t: "s",
            s: {
              font: { bold: true },
            },
          },
          D: {
            v: `Mètre plancher linéaire`,
            t: "s",
            s: tableHeaderConfig,
          },
          E: {
            v: `Hauteur (cm)`,
            t: "s",
            s: tableHeaderConfig,
          },
          F: "",
          G: {
            v: `Quantité`,
            t: "s",
            s: tableHeaderConfig,
          },
          H: {
            v: `Poids total (kg)`,
            t: "s",
            s: tableHeaderConfig,
          },
        });

        // Total Bs
        rows.push({
          A: "",
          B: "",
          C: "",
          D: {
            v: totalValues.loadingMeters || "",
            t: "s",
            s: tableCellConfig,
          },
          E: {
            v: totalValues.height || "",
            t: "s",
            s: tableCellConfig,
          },
          F: "",
          G: {
            v: totalValues.quantity || "",
            t: "s",
            s: tableCellConfig,
          },
          H: {
            v: totalValues.weight || "",
            t: "s",
            s: tableCellConfig,
          },
        });

        // Empty row
        rows.push(emptyLine);
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, shippingRecord?.code);

    /* fix headers */
    XLSX.utils.sheet_add_aoa(worksheet, [["", "", "", "", "", "", "", ""]], {
      origin: "A1",
    });

    worksheet["!cols"] = [
      { wch: 60 },
      { wch: 80 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];

    XLSX.writeFile(workbook, `${shippingRecord?.code}.xlsx`);
  }, [shippingRecord, packageTypes]);

  const exportShippingRecordAffretement = useCallback(async () => {
    if (!shippingRecord) return;
    const firstShipment = shippingRecord.shipments[0];
    if (!firstShipment) return;

    // firstShipment.op.entrepot is a lean OP-embedded projection (id/name/
    // location only, no delivery hours or users) — refetch it in full the
    // same way the delivery entrepot is, so hours/contact/phone/email work.
    const pickupEntrepot = await fetchEntrepotByName(
      firstShipment.op?.entrepot?.name
    );

    // The template only has 3 delivery slots (LIVRAISON 1/2/3) — a dossier
    // with more than 3 distinct delivery locations only reports the first 3.
    const deliveryGroups = groupShipmentsByDelivery(
      shippingRecord.shipments
    ).slice(0, AFFRETEMENT_DELIVERY_COLUMNS.length);
    const deliveryDetailsList = await Promise.all(
      deliveryGroups.map((group) => resolveDeliveryDetails(group[0], token))
    );

    const pickupAddress =
      `${pickupEntrepot?.location?.adress || ""} ${
        pickupEntrepot?.location?.complement || ""
      }`.trim() || "-";
    const pickupName = pickupEntrepot?.name || "-";
    const pickupEntrepotContact = await fetchEntrepotContact(
      token,
      pickupEntrepot?._id
    );

    // One row per shipment/OP (the dossier's colisage table row count should
    // match its number of OPs). The template's real headers (verified against
    // the raw sheet XML/sharedStrings, not just eyeballing column letters) are
    // C="Livraison", D="Colisage", E="HAUTEUR (CM)", F="POIDS (KG)",
    // G="AIRE (MPL)" (blank on purpose), H="Dossier" (left blank — no per-row
    // use for it) — so C gets the 1/2/3 index matching the LIVRAISON block
    // above (deliveryGroups is index-aligned with
    // AFFRETEMENT_DELIVERY_COLUMNS/deliveryDetailsList). A shipment whose
    // delivery location didn't make it into the first 3 groups gets no
    // number in C, since it isn't represented in the header block either.
    // Shipment.parcel is normally embedded, but a shipment whose parcelId FK
    // was never synced won't have it even though a Parcel row exists (linked
    // instead via Parcel.opId) — fall back to fetching it that way.
    const shipmentParcels = await Promise.all(
      shippingRecord.shipments.map(async (shipment) => ({
        shipment,
        parcel:
          shipment.parcel ||
          (await ParcelService.getParcelByOpId(shipment.opId)),
      }))
    );

    type ColisageTableRow = {
      description: string;
      height: number | "";
      weight: number | "";
      deliveryNumber: number | "";
    };

    const colisageDataRows: ColisageTableRow[] = shipmentParcels
      .filter(({ parcel }) => parcel && !(parcel as { error?: unknown }).error)
      .map(({ shipment, parcel }) => {
        const deliveryIndex = deliveryGroups.findIndex((group) =>
          group.includes(shipment)
        );
        const deliveryNumber: number | "" =
          deliveryIndex === -1 ? "" : deliveryIndex + 1;

        // A Parcel can legitimately exist with zero Package rows (its own
        // height/weight/quantity set directly) — the shipping service has a
        // dedicated EMPTY_PARCEL case for this. Fall back to those
        // parcel-level totals instead of producing no description at all.
        if (!parcel.packages || parcel.packages.length === 0) {
          return {
            description: `${parcel.quantity || 0} colis`,
            height: parcel.height || 0,
            weight: parcel.weight || 0,
            deliveryNumber,
          };
        }

        const dimensionGroups: {
          label: string;
          width: number;
          length: number;
          quantity: number;
        }[] = [];
        parcel.packages.forEach((packageItem: any) => {
          const packageType = packageTypes.find(
            (type) => type.id === packageItem.typeId
          );
          const existingGroup = dimensionGroups.find(
            (group) =>
              group.width === packageItem.width &&
              group.length === packageItem.length
          );
          if (existingGroup) {
            existingGroup.quantity += packageItem.quantity || 0;
          } else {
            dimensionGroups.push({
              label: packageType?.label || "Colis",
              width: packageItem.width,
              length: packageItem.length,
              quantity: packageItem.quantity || 0,
            });
          }
        });

        return {
          description: dimensionGroups
            .map(
              (group) =>
                `${group.quantity} ${group.label} ${group.width}x${group.length}`
            )
            .join(" + "),
          height: parcel.height || 0,
          weight: parcel.weight || 0,
          deliveryNumber,
        };
      })
      // LIVRAISON 1, then 2, then 3; shipments with no mapped slot last.
      .sort((a, b) => {
        const rank = (n: number | "") =>
          n === "" ? Number.POSITIVE_INFINITY : n;
        return rank(a.deliveryNumber) - rank(b.deliveryNumber);
      });

    // After each delivery-location block, insert a Total row (Colisage +
    // summed Poids). The template only has 10 body rows (21–30).
    const colisageRows: ColisageTableRow[] = [];
    let groupRows: ColisageTableRow[] = [];
    let groupDelivery: number | "" | undefined;

    const flushDeliveryGroup = () => {
      if (groupRows.length === 0) return;
      colisageRows.push(...groupRows);
      colisageRows.push({
        description: "Total",
        height: "",
        weight: groupRows.reduce(
          (total, row) =>
            total + (typeof row.weight === "number" ? row.weight : 0),
          0
        ),
        deliveryNumber: "",
      });
      groupRows = [];
    };

    colisageDataRows.forEach((row) => {
      if (groupDelivery !== undefined && row.deliveryNumber !== groupDelivery) {
        flushDeliveryGroup();
      }
      groupDelivery = row.deliveryNumber;
      groupRows.push(row);
    });
    flushDeliveryGroup();

    const dossierNumber = shippingRecord.code?.replace(/^DOS/i, "");

    // Edit the template's raw worksheet XML in place (rather than going through
    // xlsx-js-style's object model) so styles, theme colors, and the embedded
    // logo image are preserved byte-for-byte instead of being rebuilt and lost.
    const templateResponse = await fetch(
      "/templates/affretement-template.xlsx"
    );
    const templateBuffer = await templateResponse.arrayBuffer();
    const zip = await JSZip.loadAsync(templateBuffer);
    const sheetPath = await resolveSheetPath(zip, "Modèle");
    if (!sheetPath) return;
    let sheetXml = await zip.file(sheetPath).async("string");
    const stylesFile = zip.file("xl/styles.xml");
    if (!stylesFile) return;
    const { stylesXml, dossierStyleId, livraisonStyleId, colisageStyleId } =
      addAffretementFilledCellStyles(await stylesFile.async("string"));
    zip.file("xl/styles.xml", stylesXml);

    // Read-only parse (never written back) just to check whether the pickup
    // name already exists in the Expéditeur address book, in which case its
    // VLOOKUP formulas should be left in place instead of being overridden.
    const lookupWorkbook = XLSX.read(new Uint8Array(templateBuffer), {
      type: "array",
    });
    const expediteurRows = readAddressBookRows(lookupWorkbook, "Expéditeur");
    const pickupFoundInAddressBook = expediteurRows.some(
      (row) =>
        normalizeText((row[0] || "").toString()) === normalizeText(pickupName)
    );

    type FilledCell = [string, string | number, number?];
    const cellValues: FilledCell[] = [
      ["C4", dossierNumber, dossierStyleId],
      ["B9", pickupName],
    ];
    if (!pickupFoundInAddressBook) {
      cellValues.push(
        ["B10", formatEntrepotHours(pickupEntrepot)],
        ["B11", pickupAddress],
        ["B12", pickupEntrepot?.location?.cp || "-"],
        ["B13", pickupEntrepot?.location?.city || "-"],
        ["B14", formatContactName(pickupEntrepotContact)],
        ["B15", pickupEntrepotContact?.phone || "-"],
        ["B16", pickupEntrepotContact?.email || "-"]
      );
    }
    // B17 ("Information accès") and B18 ("Instructions complémentaires")
    // stay empty: they are free-text fields for the freight forwarder.

    // One column per distinct delivery location (G = LIVRAISON 1, H =
    // LIVRAISON 2, I = LIVRAISON 3). Always written from Noria data — the
    // Destinataire address book sheet was removed from the template.
    deliveryDetailsList.forEach((delivery, index) => {
      const column = AFFRETEMENT_DELIVERY_COLUMNS[index];
      cellValues.push(
        [`${column}9`, delivery.name, livraisonStyleId],
        [`${column}10`, delivery.hours, livraisonStyleId],
        [`${column}11`, delivery.address, livraisonStyleId],
        [`${column}12`, delivery.zip, livraisonStyleId],
        [`${column}13`, delivery.city, livraisonStyleId],
        [`${column}14`, delivery.contact, livraisonStyleId],
        [`${column}15`, delivery.phone, livraisonStyleId],
        [`${column}16`, delivery.email, livraisonStyleId]
      );
    });

    colisageRows.slice(0, 10).forEach((colisageRow, index) => {
      const row = 21 + index;
      cellValues.push(
        [`C${row}`, colisageRow.deliveryNumber, colisageStyleId],
        [`D${row}`, colisageRow.description, colisageStyleId],
        [`E${row}`, colisageRow.height, colisageStyleId],
        [`F${row}`, colisageRow.weight, colisageStyleId]
      );
    });
    // F32's own SUM(F21:F30) formula isn't reliably recalculated once opened
    // (same reason every other formula cell in this file is overridden with
    // a static value instead), so compute the grand total from data rows only
    // (subtotal "Total" rows must not be counted twice).
    cellValues.push([
      "F32",
      colisageDataRows.reduce(
        (total, row) =>
          total + (typeof row.weight === "number" ? row.weight : 0),
        0
      ),
    ]);

    cellValues.forEach(([address, value, styleId]) => {
      sheetXml = setSheetCellValue(sheetXml, address, value, styleId);
    });

    // The template carries a stray "list" data validation over B12:B16 (and
    // some label cells) with an invalid source (formula1="="), left over
    // from the original file. Excel still shows a dropdown selector on those
    // cells even though the list itself is broken, so strip it entirely.
    sheetXml = sheetXml.replace(
      /<dataValidations[\s\S]*?<\/dataValidations>/,
      ""
    );

    zip.file(sheetPath, sheetXml);
    await removeCalcChain(zip);
    const outputBlob = await zip.generateAsync({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      compression: "DEFLATE",
    });

    const downloadUrl = URL.createObjectURL(outputBlob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${shippingRecord?.code}-affretement.xlsx`;
    link.click();
    URL.revokeObjectURL(downloadUrl);
  }, [shippingRecord, packageTypes, token]);

  return { exportShippingRecord, exportShippingRecordAffretement };
};

export default useShippingRecordExport;
